import { Injectable, ViewContainerRef } from '@angular/core';
import { Observable, of, Subject, throwError } from 'rxjs';
import { HttpClient } from '@angular/common/http';
import { KeyedResource } from '@resources/keyed.resource';
import { LMErrorUtilService, LMUrlUtilService } from '@services/utils';
import { AppConstants, FormRequestType, LMDialogButtonKey, LMUniqueIdService } from '@utils/index';
import { catchError } from 'rxjs/operators';
import {
  LMCubeCellRelationDefinition,
  LMPrintLPL
} from '@modal/cube-cell-rel/lm-cube-cell-rel';
import {
  LMCubeRequest,
  LMDimension, LMDimensionMappingResult,
  LMMeasure,
} from '@modal/cubes/lm-cubes';
import { LMLoggerService } from '@services/lm-logger.service';
import { SohoModalDialogService } from 'ids-enterprise-ng';
import { LMCubeDimensionsMappingComponent } from '@modal/cubes/lm-cube-dimensions-mapping.component';
import { LMCubeCellRelTestComponent } from '@modal/cube-cell-rel/lm-cube-cell-rel-test.component';

@Injectable()
export class LMCubeCellRelService {
  constructor(
    private http: HttpClient,
    private sohoModalDialogService: SohoModalDialogService,
    private lmUrlUtilService: LMUrlUtilService,
    private lmLoggerService: LMLoggerService,
    private lmErrorUtilService: LMErrorUtilService,
    private lmUniqueIdService: LMUniqueIdService,
  ) {}

  public buildCubeCellRelDimensionsDialog(placeHolder: ViewContainerRef,
    busClassTotal: string,
    busClass: string,
    definition: string,
    dimensionMappingInput?: Array<LMDimension>): Observable<LMCubeCellRelationDefinition> {
    const relationDefinition = new Subject<LMCubeCellRelationDefinition>();
    const closeModalWith = (result: LMCubeCellRelationDefinition, modal) => {
      modal.close(true);
      relationDefinition.next(result);
      relationDefinition.complete();
    }

    this.getDimensionMapping(busClass, definition, dimensionMappingInput)
      .subscribe((dimensionMappingResult: LMDimensionMappingResult) => {
        let cubeCellRelDimensionsDialogRef: LMCubeDimensionsMappingComponent;

        const buttons = [
          {
            id: this.lmUniqueIdService.uniqueId(['lm-cube-cell-rel', 'dimensions', LMDialogButtonKey.CANCEL]),
            text: Soho.Locale.translate('Cancel'),
            click: (e, modal) => {
              closeModalWith(undefined, modal);
            }
          },
          {
            id: this.lmUniqueIdService.uniqueId(['lm-cube-cell-rel', 'dimensions', LMDialogButtonKey.OK]),
            text: Soho.Locale.translate('Ok'),
            click: (e, modal) => {
              const newDimensionMapping = cubeCellRelDimensionsDialogRef.getDimensionMapping();
              this.getRelationLPL(busClass, busClassTotal, newDimensionMapping)
                .subscribe((lplResponse: LMPrintLPL) => {
                  if ((lplResponse as any).status === AppConstants.STATUS_SYSTEM_ERROR) {
                    const lmError = (lplResponse as any);
                    this.lmErrorUtilService.handleError(lmError.message, lmError.details, lmError.status);
                    return;
                  }
                  const result: LMCubeCellRelationDefinition = {dimensionMapping: newDimensionMapping, relationLpl: lplResponse.relationLPL};
                  closeModalWith(result, modal);
                },
                error => {
                  this.lmErrorUtilService.handleUnexpectedError(error, placeHolder);
                });
            },
            isDefault: true,
          },
        ];

        const dialogTitle = busClass + ' ' + Soho.Locale.translate('Dimensions');
        this.sohoModalDialogService
          .modal(LMCubeDimensionsMappingComponent, placeHolder, {fullsize: 'responsive'})
          .title(dialogTitle)
          .buttons(buttons)
          .open()
          .apply((dialogComp: LMCubeDimensionsMappingComponent) => {
            cubeCellRelDimensionsDialogRef = dialogComp;
            cubeCellRelDimensionsDialogRef.cubeBusClass = busClass;
            cubeCellRelDimensionsDialogRef.message = dimensionMappingResult.message;
            // deep clone dimension mapping. On ok, the dimension mapping will be updated.
            cubeCellRelDimensionsDialogRef.dimensionMapping = JSON.parse(JSON.stringify(dimensionMappingResult.dimensionMapping));
          });
      });

    return relationDefinition.asObservable();
  }

  private getDimensionMapping(busClass: string, definition: string, dimensionMapping?: Array<LMDimension>): Observable<LMDimensionMappingResult> {
    if (!dimensionMapping) {
      const cubeCellRequest = Object.assign(
        {requestType: FormRequestType.CubeRelDimensionMapping},
        {busClass},
        {definition});
      return this.executeFormRequest(this.getRequestUrl(busClass), cubeCellRequest);
    }
    return of({dimensionMapping, message: 'cache'});
  }

  public getRequestUrl(busClass: string): string {
    const formResource = new KeyedResource({resourceType: 'form', resourceId: busClass});
    return this.lmUrlUtilService.buildUrl({ resource: formResource });
  }

  private executeFormRequest(url: string, cubeCellRequest: LMCubeRequest): Observable<any> {
    return this.http
      .post(url, cubeCellRequest).pipe(
        catchError(error => {
          this.lmLoggerService.error(error);
          return throwError(error || 'Server error');
        }));
  }

  private getRelationLPL(busClass: string, busClassTotal: string, dimensionMapping?: Array<LMDimension>): Observable<LMPrintLPL> {
    const cubeCellRequest = Object.assign(
      {requestType: FormRequestType.CubeRelationLPL},
      {busClass},
      {busClassTotalName: busClassTotal},
      {dimensionMapping});
    return this.executeFormRequest(this.getRequestUrl(busClass), cubeCellRequest);
  }

  public getMeasures(busClass: string, measures?: Array<LMMeasure>): Observable<Array<LMMeasure>> {
    if (!measures) {
      const cubeCellRequest = Object.assign(
        {requestType: FormRequestType.CubeRelGetMeasures},
        {busClass});
      return this.executeFormRequest(this.getRequestUrl(busClass), cubeCellRequest);
    } else {
      return of(measures);
    }
  }

  public testMeasure(busClass: string, busClassTotalName: string, measure: string): Observable<any> {
    const cubeCellRequest = Object.assign(
      {requestType: FormRequestType.CubeRelTestMeasure},
      {busClass},
      {busClassTotalName},
      {measure});
    return this.executeFormRequest(this.getRequestUrl(busClass), cubeCellRequest);
  }

  public buildCubeCellRelMeasuresTestDialog(placeHolder: ViewContainerRef,
    busClassTotal: string,
    busClass: string,
    measuresInput?: Array<LMMeasure>): Observable<Array<LMMeasure>> {
    const testMeasures = new Subject<Array<LMMeasure>>();
    const closeModalWith = (result: Array<LMMeasure>, modal) => {
      modal.close(true);
      testMeasures.next(result);
      testMeasures.complete();
    }
    this.getMeasures(busClass, measuresInput)
      .subscribe((measures: Array<LMMeasure>) => {

        let cubeCellRelTestComponent: LMCubeCellRelTestComponent;

        const buttons = [
        // {
        //   id: this.lmUniqueIdService.uniqueId(['lm-cube-cell-rel', 'measures', LMDialogButtonKey.CANCEL]),
        //   text: Soho.Locale.translate('Cancel'),
        //   click: (e, modal) => {
        //     modal.close(true);
        //   }
        // },
          {
            id: this.lmUniqueIdService.uniqueId(['lm-cube-cell-rel', 'measures', LMDialogButtonKey.OK]),
            text: Soho.Locale.translate('Close'),
            click: (e, modal) => {
              closeModalWith(measures, modal);
            },
            isDefault: true,
          },
        ];

        const dialogTitle = busClass + ' ' + Soho.Locale.translate('Measures');
        this.sohoModalDialogService
          .modal(LMCubeCellRelTestComponent, placeHolder, {fullsize: 'responsive'})
          .title(dialogTitle)
          .buttons(buttons)
          .open()
          .apply((dialogComp: LMCubeCellRelTestComponent) => {
            cubeCellRelTestComponent = dialogComp;
            cubeCellRelTestComponent.busClass = busClass;
            cubeCellRelTestComponent.busClassTotalName = busClassTotal;
            cubeCellRelTestComponent.measures = measures;
          });

      });
    return testMeasures.asObservable();
  }

}
